import { DatePicker, Dropdown, InputNumber, Select, Tag } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useGeckoAnalytics } from 'features/analytics'
import { TimeRange } from 'features/systemLens'
import { TimeSeriesService } from 'features/timeSeries'
import {
  Activity,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Database,
  TrendingUp,
  Zap,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { ChartTimeUnit } from 'types'

const { RangePicker } = DatePicker

const TimeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const QuickSelectContainer = styled.div`
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  align-items: center;
`

const TimeRangeButton = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: ${({ $isActive, theme }) =>
    $isActive ? 'rgba(0, 194, 144, 0.1)' : theme.forecast.background2};
  color: ${({ $isActive, theme }) =>
    $isActive ? '#00c290' : theme.forecast.text1};
  border: 1px solid
    ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.3)' : theme.forecast.border};
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.15)' : theme.forecast.background3};
    border-color: ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.5)' : theme.forecast.text2};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: ${({ theme }) => theme.forecast.text2};
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const ChevronIcon = styled.span`
  margin-left: 0.25rem;
  opacity: 0.7;
`

const DropdownButton = styled.button<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: ${({ $isOpen, theme }) =>
    $isOpen ? theme.forecast.background3 : theme.forecast.background2};
  color: ${({ theme }) => theme.forecast.text1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
    border-color: ${({ theme }) => theme.forecast.text2};
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const DropdownContent = styled.div`
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  padding: 0.75rem;
  min-width: 18.75rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.4);
`

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const PresetLabel = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const PresetMainText = styled.div`
  font-size: 0.8125rem;
  margin-bottom: 0.125rem;
`

const PresetSubText = styled.div`
  font-size: 0.625rem;
  opacity: 0.7;
`

const PresetButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: ${({ $isActive, theme }) =>
    $isActive ? 'rgba(0, 194, 144, 0.1)' : theme.forecast.background3};
  color: ${({ $isActive, theme }) =>
    $isActive ? '#00c290' : theme.forecast.text1};
  border: 1px solid
    ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.3)' : theme.forecast.border};
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover:not(:disabled) {
    background: ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.15)' : theme.forecast.pageBackground1};
    border-color: ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.5)' : theme.forecast.text2};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: ${({ theme }) => theme.forecast.text2};
  }

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`

const CustomRangeSection = styled.div`
  padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

const SectionLabel = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.text2};
  margin-bottom: 0.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03125rem;
`

const StyledRangePicker = styled(RangePicker)`
  width: 100%;
  background: ${({ theme }) => theme.forecast.background3} !important;
  border-color: ${({ theme }) => theme.forecast.border} !important;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.text2} !important;
  }

  &.ant-picker-focused {
    border-color: #00c290 !important;
    box-shadow: 0 0 0 2px rgba(0, 194, 144, 0.1) !important;
  }

  .ant-picker-input > input {
    color: ${({ theme }) => theme.forecast.text1} !important;
    background: transparent !important;
  }

  .ant-picker-suffix {
    color: ${({ theme }) => theme.forecast.text2} !important;
  }

  .ant-picker-separator {
    color: ${({ theme }) => theme.forecast.text2} !important;
  }
`

const RangeText = styled.span`
  color: #00c290;
  font-weight: 600;
`

const DurationText = styled.span`
  color: ${({ theme }) => theme.forecast.text2};
  font-size: 0.6875rem;
`

const ActiveRangeDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.forecast.text1};
  font-size: 0.75rem;
`

const IntervalSection = styled.div`
  padding: 0.75rem 0;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
  margin-top: 0.75rem;
`

const IntervalSelectorContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const StyledIntervalInput = styled(InputNumber)`
  width: 5rem;
  background: ${({ theme }) => theme.forecast.background3} !important;

  &.ant-input-number {
    border-color: ${({ theme }) => theme.forecast.border};
  }

  &:hover {
    border-color: ${({ theme }) => theme.forecast.text2} !important;
  }

  &.ant-input-number-focused {
    border-color: #00c290 !important;
    box-shadow: 0 0 0 2px rgba(0, 194, 144, 0.1) !important;
  }

  .ant-input-number-input {
    color: ${({ theme }) => theme.forecast.text1} !important;
    background: transparent !important;
  }
`

const StyledIntervalSelect = styled(Select)`
  width: 6.5rem;

  .ant-select-selector {
    background: ${({ theme }) => theme.forecast.background3} !important;
    border-color: ${({ theme }) => theme.forecast.border} !important;
    color: ${({ theme }) => theme.forecast.text1} !important;
  }

  &:hover .ant-select-selector {
    border-color: ${({ theme }) => theme.forecast.text2} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: #00c290 !important;
    box-shadow: 0 0 0 2px rgba(0, 194, 144, 0.1) !important;
  }
`

const TimeNavButton = styled.button<{ $disabled?: boolean; $small?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $small }) => ($small ? '1.25rem' : '1.5rem')};
  height: ${({ $small }) => ($small ? '1.25rem' : '1.5rem')};
  padding: 0;
  background: ${({ theme }) => theme.forecast.background3};
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.forecast.text2 : theme.forecast.text1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.forecast.background2};
    border-color: #00c290;
    color: #00c290;
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

interface PredefinedRange {
  label: string
  value: string
  icon: React.ReactNode
  getRange: () => [Dayjs, Dayjs]
  description?: string
}

const predefinedRanges: PredefinedRange[] = [
  {
    label: 'Last Hour',
    value: 'last-hour',
    icon: <Clock />,
    getRange: () => [dayjs().subtract(1, 'hour'), dayjs()],
    description: 'Real-time monitoring',
  },
  {
    label: 'Last 8 Hours',
    value: 'last-8-hours',
    icon: <Zap />,
    getRange: () => [dayjs().subtract(8, 'hours'), dayjs()],
    description: 'Current shift',
  },
  {
    label: 'Last 24 Hours',
    value: 'last-24-hours',
    icon: <Activity />,
    getRange: () => [dayjs().subtract(24, 'hours'), dayjs()],
    description: 'Daily overview',
  },
  {
    label: 'Last 7 Days',
    value: 'last-7-days',
    icon: <Calendar />,
    getRange: () => [dayjs().subtract(7, 'days'), dayjs()],
    description: 'Weekly trend',
  },
  {
    label: 'Last 30 Days',
    value: 'last-30-days',
    icon: <TrendingUp />,
    getRange: () => [dayjs().subtract(30, 'days'), dayjs()],
    description: 'Monthly analysis',
  },
  {
    label: 'Last 3 Months',
    value: 'last-3-months',
    icon: <Database />,
    getRange: () => [dayjs().subtract(3, 'months'), dayjs()],
    description: 'Quarterly review',
  },
]

interface TimeRangeSelectorProps {
  timeRange: TimeRange
  setTimeRange: (range: TimeRange) => void
  unitID?: string
  unitName?: string
  siteID?: string
  showCompact?: boolean
  showNavigation?: boolean
  pageName?: string
}

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  timeRange,
  setTimeRange,
  unitID,
  unitName,
  siteID,
  showCompact = false,
  showNavigation = false,
  pageName,
}) => {
  const [selectedPreset, setSelectedPreset] = useState<PredefinedRange | null>(
    null
  )
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [intervalValue, setIntervalValue] = useState<number>(15)
  const [intervalUnit, setIntervalUnit] = useState<ChartTimeUnit>(
    ChartTimeUnit.MINUTES
  )
  const { trackEvent } = useGeckoAnalytics()

  const { fromTime, toTime } = useMemo(() => {
    return { fromTime: dayjs(timeRange.from), toTime: dayjs(timeRange.to) }
  }, [timeRange.from, timeRange.to])
  // Create interval options from the IntervalUnit enum
  // This ensures the options always stay in sync with the IntervalUnit type
  const intervalOptions = useMemo(() => {
    const capitalizeFirst = (str: string) =>
      str.charAt(0).toUpperCase() + str.slice(1)

    // Generate options from all IntervalUnit enum values
    // e.g., 'seconds' -> { label: 'Seconds', value: 'seconds' }
    return [
      ChartTimeUnit.SECONDS,
      ChartTimeUnit.MINUTES,
      ChartTimeUnit.HOURS,
      ChartTimeUnit.DAYS,
    ].map((unit) => ({
      label: capitalizeFirst(unit),
      value: unit,
    }))
  }, [])
  // Calculate recommended interval based on time range
  useEffect(() => {
    const timeRangeSeconds = toTime.diff(fromTime, 'seconds')
    const { interval, interval_unit } =
      TimeSeriesService.getInterval(timeRangeSeconds)

    // Only update if not already set in timeRange
    if (!timeRange.interval || !timeRange.intervalUnit) {
      setIntervalValue(interval)
      setIntervalUnit(interval_unit)
      // Update the timeRange with calculated interval
      setTimeRange({
        ...timeRange,
        interval,
        intervalUnit: interval_unit,
      })
    } else {
      setIntervalValue(timeRange.interval)
      setIntervalUnit(timeRange.intervalUnit)
    }
  }, [setTimeRange, timeRange, fromTime, toTime])

  // Determine active preset based on current time range
  useEffect(() => {
    if (selectedPreset) return
    // Check if current range matches any preset (within 1 minute tolerance)
    const matchingPreset = predefinedRanges.find((range) => {
      const [presetFrom, presetTo] = range.getRange()
      return (
        Math.abs(fromTime.diff(presetFrom, 'minute')) <= 1 &&
        Math.abs(toTime.diff(presetTo, 'minute')) <= 1
      )
    })

    setSelectedPreset(matchingPreset ?? null)
  }, [selectedPreset, fromTime, toTime])

  const handlePresetSelect = (range: PredefinedRange) => {
    setSelectedPreset(range)
    const [from, to] = range.getRange()

    // Use 15 min as default interval for all presets (dependent on influx performance)
    const interval = 15
    const interval_unit = ChartTimeUnit.MINUTES
    setIntervalValue(interval)
    setIntervalUnit(interval_unit)

    setTimeRange({
      from: from.toISOString(),
      to: to.toISOString(),
      preset: range.value as
        | 'last-hour'
        | 'last-8-hours'
        | 'last-24-hours'
        | 'last-7-days'
        | 'last-30-days'
        | 'last-3-months',
      interval,
      intervalUnit: interval_unit,
    })
    setDropdownOpen(false)

    // Track time range change
    trackEvent('Time Range Changed', {
      time_range: range.value,
      time_range_type: 'preset',
      start_date: from.toISOString(),
      end_date: to.toISOString(),
      interval_value: interval,
      interval_unit: interval_unit,
      unit_id: unitID,
      unit_name: unitName,
      site_id: siteID,
      page: pageName,
    })
  }

  const handleCustomRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      setSelectedPreset(null)
      setTimeRange({
        from: dates[0].toISOString(),
        to: dates[1].toISOString(),
        preset: 'custom',
        interval: intervalValue,
        intervalUnit: intervalUnit,
      })

      // Track custom time range change
      trackEvent('Time Range Changed', {
        time_range: 'custom',
        time_range_type: 'custom',
        start_date: dates[0].toISOString(),
        end_date: dates[1].toISOString(),
        duration_hours: dates[1].diff(dates[0], 'hours'),
        unit_id: unitID,
        unit_name: unitName,
        site_id: siteID,
        page: pageName,
      })
    }
  }

  const handleIntervalChange = (value: number | string | null) => {
    const numValue = typeof value === 'string' ? parseInt(value) : value
    if (numValue && numValue > 0) {
      setIntervalValue(numValue)
      setTimeRange({
        ...timeRange,
        interval: numValue,
        intervalUnit: intervalUnit,
      })

      // Track interval change
      trackEvent('Interval Changed', {
        interval_value: numValue,
        interval_unit: intervalUnit,
        unit_id: unitID,
        unit_name: unitName,
        site_id: siteID,
        page: pageName,
      })
    }
  }

  const handleIntervalUnitChange = (value: unknown) => {
    const unit = value as ChartTimeUnit
    setIntervalUnit(unit)
    setTimeRange({
      ...timeRange,
      interval: intervalValue,
      intervalUnit: unit,
    })

    // Track interval unit change
    trackEvent('Interval Unit Changed', {
      interval_value: intervalValue,
      interval_unit: unit,
      unit_id: unitID,
      unit_name: unitName,
      site_id: siteID,
      page: pageName,
    })
  }

  const formatDuration = (from: Dayjs, to: Dayjs) => {
    const hours = to.diff(from, 'hours')
    const days = to.diff(from, 'days')
    const months = to.diff(from, 'months')

    if (hours < 24) {
      return `${hours}h`
    } else if (days < 31) {
      return `${days}d`
    } else {
      return `${months}mo`
    }
  }

  // Navigation functions
  const navigateTimeRange = (
    direction: 'forward' | 'backward',
    zoom: boolean = false
  ) => {
    const timeDiff = toTime.diff(fromTime, 'minutes')

    let newFrom: Dayjs
    let newTo: Dayjs

    if (direction === 'backward') {
      newFrom = fromTime.subtract(timeDiff, 'minutes')
      newTo = toTime.subtract(timeDiff, 'minutes')
    } else {
      if (zoom) {
        newFrom = fromTime.add(timeDiff / 2, 'minutes')
        newTo = toTime
      } else {
        newFrom = fromTime.add(timeDiff, 'minutes')
        newTo = toTime.add(timeDiff, 'minutes')
      }
    }

    // Don't allow navigating beyond current time
    const now = dayjs()
    if (newTo.isAfter(now)) {
      newTo = now
    }

    setTimeRange({
      from: newFrom.toISOString(),
      to: !zoom ? newTo.toISOString() : timeRange.to,
      preset: 'custom',
      interval: intervalValue,
      intervalUnit: intervalUnit,
    })
  }

  // Check if we can navigate forward (not at current time)
  const canNavigateForward = () => {
    const now = dayjs()
    // Allow forward navigation if end time is more than 5 minutes before now
    return toTime.isBefore(now.subtract(5, 'minutes'))
  }

  const getCurrentLabel = () => {
    return selectedPreset?.label || 'Custom Range'
  }

  const ActiveRangeDisplay = () => {
    return (
      <ActiveRangeDisplayContainer>
        {showNavigation && (
          <TimeNavButton
            onClick={() => navigateTimeRange('backward')}
            title='Previous time period'>
            <ChevronLeft />
          </TimeNavButton>
        )}
        <RangeText>
          {fromTime.format('MMM DD HH:mm')} - {toTime.format('MMM DD HH:mm')}
        </RangeText>
        <DurationText>({formatDuration(fromTime, toTime)})</DurationText>
        {timeRange.preset === 'custom' && <Tag color='green'>Custom</Tag>}
        {showNavigation && (
          <div>
            <TimeNavButton
              onClick={() => navigateTimeRange('forward')}
              disabled={!canNavigateForward()}
              $disabled={!canNavigateForward()}
              title='Next time period'>
              <ChevronRight />
            </TimeNavButton>
          </div>
        )}
        {showNavigation && (
          <div>
            <TimeNavButton
              title='Zoom Out'
              $small
              onClick={() => navigateTimeRange('backward', true)}>
              <ZoomOut />
            </TimeNavButton>
            <TimeNavButton
              title='Zoom In'
              $small
              onClick={() => navigateTimeRange('forward', true)}>
              <ZoomIn />
            </TimeNavButton>
          </div>
        )}
      </ActiveRangeDisplayContainer>
    )
  }
  if (showCompact) {
    // Compact view with quick select buttons
    return (
      <TimeRangeContainer>
        <QuickSelectContainer>
          {predefinedRanges.slice(0, 4).map((range) => {
            return (
              <TimeRangeButton
                key={range.value}
                $isActive={selectedPreset?.value === range.value}
                onClick={() => handlePresetSelect(range)}
                title={range.description}>
                {range.icon}
                {range.label}
              </TimeRangeButton>
            )
          })}

          <Dropdown
            trigger={['click']}
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            dropdownRender={() => (
              <DropdownContent onClick={(e) => e.stopPropagation()}>
                <SectionLabel>All Time Ranges</SectionLabel>
                <PresetGrid>
                  {predefinedRanges.map((range) => {
                    return (
                      <PresetButton
                        key={range.value}
                        $isActive={selectedPreset?.value === range.value}
                        onClick={() => handlePresetSelect(range)}
                        title={range.description}>
                        {range.icon}
                        <PresetLabel>
                          <PresetMainText>{range.label}</PresetMainText>
                          <PresetSubText>{range.description}</PresetSubText>
                        </PresetLabel>
                      </PresetButton>
                    )
                  })}
                </PresetGrid>

                <CustomRangeSection>
                  <SectionLabel>Custom Range</SectionLabel>
                  <StyledRangePicker
                    showTime={{
                      format: 'HH:mm',
                    }}
                    format='YYYY-MM-DD HH:mm'
                    value={selectedPreset ? null : [fromTime, toTime]}
                    onChange={handleCustomRangeChange}
                    placeholder={['Start Time', 'End Time']}
                    allowClear
                    size='small'
                    getPopupContainer={(trigger) =>
                      trigger.parentNode as HTMLElement
                    }
                  />
                </CustomRangeSection>

                <IntervalSection>
                  <SectionLabel>Time Interval</SectionLabel>
                  <IntervalSelectorContainer>
                    <StyledIntervalInput
                      min={1}
                      value={intervalValue}
                      onChange={handleIntervalChange}
                      placeholder='Interval'
                      size='small'
                    />
                    <StyledIntervalSelect
                      value={intervalUnit}
                      onChange={handleIntervalUnitChange}
                      size='small'
                      options={intervalOptions}
                    />
                  </IntervalSelectorContainer>
                </IntervalSection>
              </DropdownContent>
            )}
            placement='bottomLeft'>
            <DropdownButton $isOpen={dropdownOpen}>
              <Database />
              More
              <ChevronIcon>
                <ChevronDown />
              </ChevronIcon>
            </DropdownButton>
          </Dropdown>
        </QuickSelectContainer>

        <ActiveRangeDisplay />
      </TimeRangeContainer>
    )
  }

  // Full view with dropdown
  return (
    <TimeRangeContainer>
      <Dropdown
        trigger={['click']}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        dropdownRender={() => (
          <DropdownContent onClick={(e) => e.stopPropagation()}>
            <SectionLabel>Quick Select</SectionLabel>
            <PresetGrid>
              {predefinedRanges.map((range) => {
                return (
                  <PresetButton
                    key={range.value}
                    $isActive={selectedPreset?.value === range.value}
                    onClick={() => handlePresetSelect(range)}
                    title={range.description}>
                    {range.icon}
                    <PresetLabel>
                      <PresetMainText>{range.label}</PresetMainText>
                      <PresetSubText>{range.description}</PresetSubText>
                    </PresetLabel>
                  </PresetButton>
                )
              })}
            </PresetGrid>

            <CustomRangeSection>
              <SectionLabel>Custom Range</SectionLabel>
              <StyledRangePicker
                showTime={{
                  format: 'HH:mm',
                }}
                format='YYYY-MM-DD HH:mm'
                value={selectedPreset ? null : [fromTime, toTime]}
                onChange={handleCustomRangeChange}
                placeholder={['Start Time', 'End Time']}
                allowClear
                size='small'
                getPopupContainer={(trigger) =>
                  trigger.parentNode as HTMLElement
                }
              />
            </CustomRangeSection>

            <IntervalSection>
              <SectionLabel>Time Interval</SectionLabel>
              <IntervalSelectorContainer>
                <StyledIntervalInput
                  min={1}
                  value={intervalValue}
                  onChange={handleIntervalChange}
                  placeholder='Interval'
                  size='small'
                />
                <StyledIntervalSelect
                  value={intervalUnit}
                  onChange={handleIntervalUnitChange}
                  size='small'
                  options={intervalOptions}
                />
              </IntervalSelectorContainer>
            </IntervalSection>
          </DropdownContent>
        )}
        placement='bottomRight'>
        <DropdownButton $isOpen={dropdownOpen}>
          <Database />
          {getCurrentLabel()}
          <ChevronIcon>
            <ChevronDown />
          </ChevronIcon>
        </DropdownButton>
      </Dropdown>
      <ActiveRangeDisplay />
    </TimeRangeContainer>
  )
}

export default TimeRangeSelector
